import type { RadioOption } from '@/components/molecules/RadioGroup/RadioGroup';
import type { CheckboxOption } from '@/components/molecules/CheckboxGroup/CheckboxGroup';

// Icons for mobile filters
import sfrIcon from '@/assets/icons/sfr.svg';
import orangeIcon from '@/assets/icons/orange.svg';
import freeIcon from '@/assets/icons/free.svg';
import bouyguesIcon from '@/assets/icons/bouygues.svg';
import padlockIcon from '@/assets/icons/padlock.svg';
import fivegIcon from '@/assets/icons/5g.svg';
import simIcon from '@/assets/icons/sim.svg';
import eSimIcon from '@/assets/icons/esim.svg';
import worldIcon from '@/assets/icons/world.svg';


// ==================== TYPES ====================

export type FilterType = 'mobile' | 'internet';

export type FilterComponentType = 'radio' | 'checkbox' | 'collapse-checkbox';

// Legacy filter config (for the old Filters system)
export interface LegacyFilterConfig {
  name: string;
  type: FilterComponentType;
  label: string;
  options: RadioOption[] | CheckboxOption[];
  defaultValue?: string | string[] | null;
}

// ==================== MOBILE FILTERS ====================

const mobileDataOptions: RadioOption[] = [
  { value: 'less-10', label: 'Moins de 10 Go', responsiveLabel: '- 10 Go' },
  { value: '20-and-more', label: '20 Go ou plus', responsiveLabel: '20 Go ou +' },
  { value: '100-and-more', label: '100 Go ou plus', responsiveLabel: '100 Go ou +' },
  { value: '300-and-more', label: '300 Go ou plus', responsiveLabel: '300 Go ou +' },
];

const mobileBudgetOptions: RadioOption[] = [
  { value: 'less-5', label: 'Moins de 5€', responsiveLabel: '- de 5€' },
  { value: 'less-10', label: 'Moins de 10€', responsiveLabel: '- de 10€' },
  { value: 'less-15', label: 'Moins de 15€', responsiveLabel: '- de 15€' },
  { value: 'more-15', label: '15€ ou plus', responsiveLabel: '15€ ou +' },
];

const mobileNetworkOptions: CheckboxOption[] = [
  { value: 'sfr', label: 'SFR', icon: sfrIcon },
  { value: 'orange', label: 'Orange', icon: orangeIcon },
  { value: 'free-mobile', label: 'Free', icon: freeIcon },
  { value: 'bouygues-telecom', label: 'Bouygues', icon: bouyguesIcon },
];

// Keys known to the predicate map in applyFilters.ts. Must stay in sync
// with MobileAdvancedOptionsRegistry::OPTIONS (theme PHP, source of truth).
export type MobileAdvancedKey =
  | 'bloquable'
  | '5gCompatible'
  | '5g-plus'
  | 'multisim'
  | 'esim'
  | 'internationalData';

const advancedIconMap: Record<string, string> = {
  padlock: padlockIcon,
  '5g': fivegIcon,
  sim: simIcon,
  esim: eSimIcon,
  world: worldIcon,
};

// Dev/storybook fallback when window.__APP_DATA__ is not injected.
const mobileAdvancedFallback: CheckboxOption[] = [
  { value: 'bloquable', label: 'Bloquable', icon: padlockIcon },
  { value: '5gCompatible', label: 'Compatible 5G', icon: fivegIcon },
  { value: '5g-plus', label: '5G+', icon: fivegIcon },
  { value: 'multisim', label: 'Multi-sim', icon: simIcon },
  { value: 'esim', label: 'eSim', icon: eSimIcon },
  { value: 'internationalData', label: 'Data internationales', icon: worldIcon },
];

function getMobileAdvancedOptions(): CheckboxOption[] {
  const serverOptions = typeof window !== 'undefined'
    ? window.__APP_DATA__?.mobileFilterOptions?.advanced
    : undefined;
  if (!serverOptions || serverOptions.length === 0) {
    return mobileAdvancedFallback;
  }
  return serverOptions.map(({ value, label, icon }) => ({
    value,
    label,
    icon: advancedIconMap[icon],
  }));
}

function getMobileOperatorOptions(): CheckboxOption[] {
  if (typeof window === 'undefined') return [];
  const ops = window.__APP_DATA__?.mobileFilterOptions?.operators;
  if (!ops || ops.length === 0) return [];
  return ops.map(({ value, label }: { value: string; label: string }) => ({
    value,
    label,
  }));
}

function buildMobileFilters(): LegacyFilterConfig[] {
  const operatorOptions = getMobileOperatorOptions();
  const filters: LegacyFilterConfig[] = [
    {
      name: 'internet-mobile',
      type: 'radio',
      label: 'Internet',
      options: mobileDataOptions,
      defaultValue: null,
    },
    {
      name: 'budget-mobile',
      type: 'radio',
      label: 'Budget',
      options: mobileBudgetOptions,
      defaultValue: null,
    },
    {
      name: 'network-mobile',
      type: 'checkbox',
      label: 'Réseau',
      options: mobileNetworkOptions,
      defaultValue: [],
    },
  ];

  if (operatorOptions.length > 0) {
    filters.push({
      name: 'operator-mobile',
      type: 'collapse-checkbox',
      label: 'Opérateur',
      options: operatorOptions,
      defaultValue: [],
    });
  }

  filters.push({
    name: 'options-mobile',
    type: 'collapse-checkbox',
    label: 'Options',
    options: getMobileAdvancedOptions(),
    defaultValue: [],
  });

  return filters;
}

// ==================== INTERNET FILTERS ====================

const internetTechnologyOptions: CheckboxOption[] = [
  { value: 'fibre', label: 'Fibre' },
  { value: 'adsl', label: 'ADSL' },
  { value: 'box-5g', label: '5G Box' },
];

const internetBudgetOptions: RadioOption[] = [
  { value: 'less-20', label: 'Moins de 20€', responsiveLabel: '- de 20€' },
  { value: 'less-30', label: 'Moins de 30€', responsiveLabel: '- de 30€' },
  { value: 'less-40', label: 'Moins de 40€', responsiveLabel: '- de 40€' },
  { value: 'more-40', label: '40€ ou plus', responsiveLabel: '40€ ou +' },
];

const internetSpeedOptions: RadioOption[] = [
  { value: '100-mbps', label: '100 Mb/s', responsiveLabel: '100 Mb/s' },
  { value: '500-mbps', label: '500 Mb/s', responsiveLabel: '500 Mb/s' },
  { value: '1-gbps', label: '1 Gb/s ou plus', responsiveLabel: '1 Gb/s+' },
];

const internetOperatorOptions: CheckboxOption[] = [
  { value: 'orange', label: 'Orange', icon: orangeIcon },
  { value: 'free', label: 'Free', icon: freeIcon },
  { value: 'sfr', label: 'SFR', icon: sfrIcon },
  { value: 'bouygues-telecom', label: 'Bouygues', icon: bouyguesIcon },
];

// Legacy config (for the old system)
export const INTERNET_FILTERS: LegacyFilterConfig[] = [
  {
    name: 'technology-internet',
    type: 'checkbox',
    label: 'Technologie',
    options: internetTechnologyOptions,
    defaultValue: [],
  },
  {
    name: 'budget-internet',
    type: 'radio',
    label: 'Budget',
    options: internetBudgetOptions,
    defaultValue: null,
  },
  {
    name: 'speed-internet',
    type: 'radio',
    label: 'Débit',
    options: internetSpeedOptions,
    defaultValue: null,
  },
  {
    name: 'network-internet',
    type: 'checkbox',
    label: 'Réseau',
    options: internetOperatorOptions,
    defaultValue: [],
  },
];

// ==================== HELPER FUNCTIONS ====================

export function getFiltersConfig(type: FilterType): LegacyFilterConfig[] {
  switch (type) {
    case 'internet':
      return INTERNET_FILTERS;
    case 'mobile':
    default:
      return buildMobileFilters();
  }
}
